import { Heading02 } from 'module/ui/components/styles';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { Button, Icon } from '@rocketlane/ui';
import { Tooltip } from 'antd';
import { useMemo } from 'react';
import styled, { css } from 'styled-components';

export const SidePaneWrapper = styled.div`
  display: flex;
  flex-shrink: 0;
  max-width: 532px;
  min-height: 0;
  align-self: stretch;
`;

export const SidePaneHeader = styled.div`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-02) var(--spacing-05);
  border-bottom: 1px solid var(--field-border-default);
  ${Heading02};
`;
export const SidePaneContent = styled.div`
  width: 488px;
  height: 100%;
  min-height: 0;
  background: var(--white-100);
  border-left: 1px solid var(--field-border-default);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

/** Fills remaining height under the header so tab content can scroll internally. */
const SidePaneBody = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const RailNavIcon = styled.span<{ $active?: boolean; $disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: var(--border-radius-default);
  ${({ $disabled }) =>
    $disabled
      ? css`
          cursor: not-allowed;
        `
      : css`
          cursor: pointer;
          &:hover {
            background-color: var(--coolest-gray-10);
          }
        `};
  ${({ $active }) =>
    $active &&
    css`
      background-color: var(--gray-10);
    `};
`;

export const RightNavBarWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  height: 100%;
  min-height: 0;
  width: 48px;
  gap: var(--spacing-01);
  border-left: 1px solid var(--divider-default);
  ${RailNavIcon} {
    margin: var(--spacing-03);
  }
`;

type SidePaneItem = {
  key: string;
  title: React.ReactNode;
  icon: React.ReactNode;
  content: React.ReactNode;
  disableManualOpen?: boolean;
};
type SidePaneProps = {
  items: SidePaneItem[];
  activeKey?: string;
  onSelect: (key: string) => void;
  onClose: () => void;
  canClose?: boolean;
};
export const MeetingPagesSidePane = ({
  items,
  activeKey,
  onSelect,
  onClose,
  canClose = true,
}: SidePaneProps) => {
  const activeItem = useMemo(
    () => items.find((item) => item.key === activeKey),
    [items, activeKey],
  );

  return (
    <SidePaneWrapper>
      {activeItem && (
        <SidePaneContent>
          <SidePaneHeader>
            {activeItem.title}
            {canClose && (
              <Button
                data-cy="meeting-pages-side-pane-close"
                variant="ghost"
                size="medium"
                onClick={onClose}
                leftIcon={<Icon icon={faXmark} size={'1x'} />}
              />
            )}
          </SidePaneHeader>
          <SidePaneBody>{activeItem.content}</SidePaneBody>
        </SidePaneContent>
      )}
      <RightNavBarWrapper>
        {items.map((item) => (
          <Tooltip key={item.key} title={item.title} placement="left">
            <RailNavIcon
              data-cy={`meeting-pages-side-pane-rail-${item.key}`}
              className={item.key === activeKey ? 'active' : ''}
              $active={item.key === activeKey}
              $disabled={item.disableManualOpen}
              onClick={() => {
                if (item.disableManualOpen) {
                  return;
                } else {
                  onSelect(item.key);
                }
              }}
            >
              {item.icon}
            </RailNavIcon>
          </Tooltip>
        ))}
      </RightNavBarWrapper>
    </SidePaneWrapper>
  );
};
